import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';

export const SITE_URL = 'https://hearthoak.org';
export const BUSINESS_ID = `${SITE_URL}/#business`;
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const match = selector.match(/\[(name|property)="([^"]+)"\]/);
    if (match) el.setAttribute(match[1], match[2]);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

// Injects one or more JSON-LD blocks into <head>, removed on unmount.
export function JsonLd({ data }) {
  const serialized = JSON.stringify(data);
  useEffect(() => {
    const blocks = (Array.isArray(data) ? data : [data]).filter(Boolean);
    const scripts = blocks.map((d) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-jsonld', 'page');
      s.text = JSON.stringify(d);
      document.head.appendChild(s);
      return s;
    });
    return () => scripts.forEach((s) => s.remove());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);
  return null;
}

// Per-page SEO: title, meta description, canonical, OG/Twitter, robots.
export function Seo({ title, description, path = '/', image, type = 'website', noindex = false, jsonLd }) {
  const serialized = JSON.stringify(jsonLd || null);
  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    const img = image || DEFAULT_IMAGE;
    document.title = title;
    if (description) {
      upsertMeta('meta[name="description"]', { content: description });
      upsertMeta('meta[property="og:description"]', { content: description });
      upsertMeta('meta[name="twitter:description"]', { content: description });
    }
    upsertMeta('meta[name="robots"]', {
      content: noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1',
    });
    upsertCanonical(url);
    upsertMeta('meta[property="og:title"]', { content: title });
    upsertMeta('meta[name="twitter:title"]', { content: title });
    upsertMeta('meta[property="og:url"]', { content: url });
    upsertMeta('meta[property="og:type"]', { content: type });
    upsertMeta('meta[property="og:site_name"]', { content: 'HearthOak Construction, LLC' });
    upsertMeta('meta[property="og:image"]', { content: img });
    upsertMeta('meta[name="twitter:card"]', { content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:image"]', { content: img });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image, type, noindex]);
  return jsonLd ? <JsonLd data={jsonLd} /> : null;
}

// Visible breadcrumb trail + matching BreadcrumbList JSON-LD.
export function Breadcrumbs({ items, testId = 'breadcrumbs' }) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.label,
      ...(it.to ? { item: `${SITE_URL}${it.to}` } : {}),
    })),
  };
  return (
    <nav aria-label="Breadcrumb" className="mb-8" data-testid={testId}>
      <JsonLd data={jsonLd} />
      <ol className="flex flex-wrap items-center gap-1.5 font-sans text-xs uppercase tracking-[0.18em] text-brand-muted">
        {items.map((it, i) => (
          <li key={it.label} className="flex items-center gap-1.5">
            {i > 0 && <Icons.ChevronRight size={12} className="text-brand-border" />}
            {it.to && i < items.length - 1 ? (
              <Link to={it.to} className="hover:text-brand-maroon transition-colors" data-testid={`${testId}-link-${i}`}>
                {it.label}
              </Link>
            ) : (
              <span className={i === items.length - 1 ? 'text-brand-charcoal' : ''}>{it.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
